const Konashi = require('../konashijs/konashi.js');
const log = require('../../util/log');

/**
 * konashiのピンを表現したクラス
 */
class _PioPin {
    constructor (pin) {
        this.number = pin;
        this.pioMode = Konashi.consts.INPUT;
        this.pullup = Konashi.consts.NO_PULLS;
        this.input = Konashi.consts.LOW;
        this.output = Konashi.consts.LOW;
        this.pwmMode = Konashi.consts.KONASHI_PWM_DISABLE;
        this.pwmPeriod = 0;
        this.pwmDuty = 0;
        this.pwmRatio = 0;
    }
}

/**
 * Web Bluetooth には Bluetooth のコマンド送受信の失敗エラーハンドリングが実装されていないため、
 * Queueとdelayを使って、逐次処理っぽいものをここで実装しています。
 */
class Queue {
    constructor () {
        this.queue = Promise.resolve(true);
        this.stopQueue = false;
    }

    add (job, delay) {
        const p = function () {
            return new Promise(resolve => {
                setTimeout(() => {
                    job();
                    resolve();
                }, delay);
            });
        };
        this.queue = this.queue.then(p);
    }
}

// 送るコマンドに応じて、送信完了にかかる時間が異なるので、複数の待ち受け時間を用意しています。
const _bleDelays = {
    NONE: 0,
    NORMAL: 50,
    LONG: 100,
    DEBUG: 1000
};

class Kokoro {
    constructor () {
        this._queue = new Queue();
        this._konashi = null;
        this._name = null;
        this._isConnectionFailed = false;

        this._isSending = 0; // 現在送信QueueにたまっているPromiseの数

        this._pioPins = [
            new _PioPin(Konashi.consts.PIO0),
            new _PioPin(Konashi.consts.PIO1),
            new _PioPin(Konashi.consts.PIO2),
            new _PioPin(Konashi.consts.PIO3),
            new _PioPin(Konashi.consts.PIO4),
            new _PioPin(Konashi.consts.PIO5),
            new _PioPin(Konashi.consts.PIO6),
            new _PioPin(Konashi.consts.PIO7)
        ];
    }

    /**
     * 接続を開始する
     * konashi の find, connect をラップしていることに加えて、
     * 初期起動時のPIN mode 書き換えも同時に行っている。
     *
     * @param {string} prefix デバイス名のプレフィックス
     * @returns {Promise<void>} 接続と初期設定の完了で解決するPromise
     */
    connect (prefix) {
        if (this._konashi) this._konashi.disconnect();

        return Konashi.funcs
            .find(true, prefix, null, () => {
                this._isConnectionFailed = true;
            })
            .then(k => {
                // 見つかったら、このクラスに実態GATTBluetoothDeviceを登録
                this._konashi = k;
                this._name = k.name();
                this._isSending = 0;
            })
            .then(() => {
                // その後、初回起動時の関数を実行
                const that = this;
                this._pioPins.forEach(pin => {
                    this._queue.add(() => {
                        that._konashi.pwmMode(
                            pin.number,
                            Konashi.consts.KONASHI_PWM_ENABLE_LED_MODE
                        );
                        pin.pwmMode =
                            Konashi.consts.KONASHI_PWM_ENABLE_LED_MODE;
                    }, _bleDelays.LONG);

                    this._queue.add(() => {
                        that._konashi.pwmLedDrive(pin.number, 0);
                        pin.pwmRatio = 0;
                    }, _bleDelays.LONG);
                });
            });
    }

    // type: 'all', 'motor', 'led'
    reset (type) {
        if (!this._konashi) return;

        const that = this;
        if (type === 'all') {
            this._pioPins.forEach(pin => {
                this._queue.add(() => {
                    that._konashi.pwmLedDrive(pin.number, 0);
                    pin.pwmRatio = 0;
                }, _bleDelays.NORMAL);
            });
        }

        if (type === 'motor') {
            this._pioPins.slice(1, 5).forEach(pin => {
                this._queue.add(() => {
                    that._konashi.pwmLedDrive(pin.number, 0);
                    pin.pwmRatio = 0;
                }, _bleDelays.NORMAL);
            });
        }

        if (type === 'led') {
            this._pioPins.slice(5, 8).forEach(pin => {
                this._queue.add(() => {
                    that._konashi.pwmLedDrive(pin.number, 0);
                    pin.pwmRatio = 0;
                }, _bleDelays.NORMAL);
            });
        }
    }

    /**
     * @returns {boolean} 接続状態
     */
    isConnected () {
        let connected = false;
        if (this._konashi) {
            connected = this._konashi.isConnected();
        }
        return connected;
    }

    isConnectionFailed () {
        return this._isConnectionFailed;
    }

    /**
     * 現在のPWM値を取得
     *
     * @param {number} pid ピン番号
     * @returns {number} 現在のPWM値
     */
    getPwmRatio (pid) {
        if (pid < 0 || pid > 7) return;

        return this._pioPins[pid].pwmRatio;
    }

    /**
     * setPwmRatio内でエラーハンドリングをする関数
     *
     * @param {Error} error 発生したエラー
     */
    _errorCallback (error) {
        log.log(`cocorokit error: ${error}`);

        const alreadyUsedError = /already in progress/;
        if (alreadyUsedError.test(error)) {
            this._queue = new Queue();
            this._isSending = 0;
            this._konashi.reset();
        }
    }

    /**
     * ピンのPWM値を変更
     *
     * @param {number} pid ピン番号
     * @param {Numebr} ratio 0 - 100
     */
    setPwmRatio (pid, ratio) {
        if (pid < 0 || pid > 7) return; // ココロキットのPINの範囲内
        if (this._isSending >= 2) return; // 現状送信しているコマンドの数が2以上なら送らない
        if (ratio == this._pioPins[pid].pwmRatio) return; // 現在のPWM値と同じなら、送信しない

        const that = this;
        that._isSending++;
        this._queue.add(() => {
            that._konashi
                .pwmLedDrive(pid, ratio) // 送信
                .then(() => {
                    // 送信したらPINが持っている値を変更、
                    that._pioPins[pid].pwmRatio = ratio;
                    if (that._isSending > 0) that._isSending--; // 送信数を1つ減らす
                })
                .catch(that._errorCallback);
        }, _bleDelays.NORMAL); // 通常のディレイを挿入
    }
}

module.exports = Kokoro;
